import { useId } from 'react';
import { LogisticsBackground } from '@/components/LogisticsBackground';
import {
  resolveThemePalette,
  type ThemeIntensity,
  type ThemePaletteId,
  type VisualPatternId,
} from '@/lib/portal-theme';

interface ThemePatternBackgroundProps {
  className?: string;
  pattern?: VisualPatternId | null;
  palette?: ThemePaletteId | null;
  intensity?: ThemeIntensity;
  tone?: 'brand' | 'onDark';
  fit?: 'cover' | 'contain';
}

const INTENSITY_OPACITY: Record<ThemeIntensity, number> = {
  low: 0.34,
  medium: 0.58,
  high: 0.82,
};

export function ThemePatternBackground({
  className = '',
  pattern,
  palette = 'ocean',
  intensity = 'medium',
  tone = 'brand',
  fit = 'cover',
}: ThemePatternBackgroundProps) {
  const patternId = pattern ?? 'port_terminal';
  const reactId = useId().replace(/:/g, '');
  const gridId = `boox-pattern-grid-${patternId}-${reactId}`;
  const dotId = `boox-pattern-dot-${patternId}-${reactId}`;
  const p = resolveThemePalette(palette);
  const opacity = INTENSITY_OPACITY[intensity];
  const c = tone === 'onDark'
    ? {
        surface: 'rgba(255,255,255,0.06)',
        motifMain: '#ffffff',
        motifSoft: '#ffffff',
        motifMuted: '#ffffff',
        accent: '#ffffff',
        text: '#ffffff',
      }
    : p;

  if (patternId === 'port_terminal') {
    return (
      <LogisticsBackground
        tint={tone === 'onDark' ? undefined : p.accent}
        fit={fit}
        className={className}
      />
    );
  }

  return (
    <svg
      className={className}
      viewBox="0 0 800 600"
      preserveAspectRatio={fit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice'}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <pattern id={gridId} width="48" height="48" patternUnits="userSpaceOnUse">
          <path d="M48 0H0v48" stroke={c.motifMuted} strokeOpacity={0.16 * opacity} strokeWidth="1" />
        </pattern>
        <pattern id={dotId} width="36" height="36" patternUnits="userSpaceOnUse">
          <circle cx="3" cy="3" r="1.8" fill={c.motifMuted} fillOpacity={0.24 * opacity} />
        </pattern>
      </defs>
      <rect width="800" height="600" fill={`url(#${gridId})`} opacity={patternId === 'minimal_grid' ? 0.8 : 0.35} />
      {renderPattern(patternId, c, opacity, dotId)}
    </svg>
  );
}

type PatternColors = {
  surface: string;
  motifMain: string;
  motifSoft: string;
  motifMuted: string;
  accent: string;
  text: string;
};

function renderPattern(pattern: VisualPatternId, c: PatternColors, opacity: number, dotId: string) {
  switch (pattern) {
    case 'rose_logistics':
      return <RoseLogistics c={c} opacity={opacity} />;
    case 'warehouse_grid':
      return <WarehouseGrid c={c} opacity={opacity} />;
    case 'container_haulage':
      return <ContainerHaulage c={c} opacity={opacity} />;
    case 'control_tower':
      return <ControlTower c={c} opacity={opacity} />;
    case 'executive_slate':
      return <ExecutiveSlate c={c} opacity={opacity} />;
    case 'coral_service':
      return <CoralService c={c} opacity={opacity} />;
    case 'minimal_grid':
      return <MinimalGrid c={c} opacity={opacity} dotId={dotId} />;
    case 'smart_inbox':
      return <SmartInbox c={c} opacity={opacity} />;
    case 'customs_docs':
      return <CustomsDocs c={c} opacity={opacity} />;
    case 'depot_gate':
      return <DepotGate c={c} opacity={opacity} />;
    case 'route_atlas':
      return <RouteAtlas c={c} opacity={opacity} dotId={dotId} />;
    case 'cold_chain':
      return <ColdChain c={c} opacity={opacity} />;
    case 'air_cargo':
      return <AirCargo c={c} opacity={opacity} />;
    case 'ledger_flow':
      return <LedgerFlow c={c} opacity={opacity} />;
    case 'ai_network':
      return <AiNetwork c={c} opacity={opacity} />;
    case 'port_terminal':
    default:
      return null;
  }
}

function RoseLogistics({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <path d="M30 500 C180 260 300 430 430 220 C560 20 700 160 780 60" stroke={c.motifMain} strokeWidth="5" strokeLinecap="round" strokeOpacity="0.46" />
      <path d="M230 340 c-70 -86 32 -158 88 -86 c58 -74 156 0 88 86 c-54 70 -120 92 -176 0z" fill={c.motifSoft} fillOpacity="0.34" />
      <path d="M520 175 c-46 -58 22 -106 58 -58 c38 -50 104 0 58 58 c-36 48 -80 62 -116 0z" fill={c.motifMain} fillOpacity="0.26" />
      <rect x="70" y="450" width="160" height="36" rx="4" fill={c.motifMain} fillOpacity="0.22" />
      <rect x="254" y="420" width="160" height="36" rx="4" fill={c.motifSoft} fillOpacity="0.24" />
      <rect x="440" y="390" width="120" height="36" rx="4" fill={c.motifMain} fillOpacity="0.18" />
    </g>
  );
}

function WarehouseGrid({ c, opacity }: { c: PatternColors; opacity: number }) {
  const racks = [70, 220, 370, 520, 670];
  return (
    <g opacity={opacity}>
      <path d="M40 500H760" stroke={c.motifMain} strokeWidth="4" strokeOpacity="0.35" />
      {racks.map((x, index) => (
        <g key={x}>
          <rect x={x} y={90 + (index % 2) * 30} width="86" height={390 - (index % 2) * 30} rx="8" fill={c.motifMain} fillOpacity="0.18" stroke={c.motifMain} strokeOpacity="0.28" />
          {[0, 1, 2, 3, 4].map((row) => (
            <rect key={row} x={x + 16} y={125 + row * 58 + (index % 2) * 20} width="54" height="28" rx="4" fill={row % 2 ? c.motifSoft : c.motifMain} fillOpacity="0.26" />
          ))}
        </g>
      ))}
      <path d="M120 520H360L400 470H520L560 520H690" stroke={c.accent} strokeWidth="9" strokeLinecap="round" strokeOpacity="0.52" />
    </g>
  );
}

function ContainerHaulage({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <path d="M20 500H780" stroke={c.motifMain} strokeWidth="14" strokeLinecap="round" strokeOpacity="0.24" />
      <path d="M40 455H760" stroke={c.motifSoft} strokeWidth="4" strokeDasharray="34 24" strokeOpacity="0.46" />
      <rect x="170" y="280" width="360" height="82" rx="8" fill={c.motifSoft} fillOpacity="0.38" />
      <rect x="548" y="306" width="104" height="72" rx="14" fill={c.motifMain} fillOpacity="0.34" />
      <rect x="570" y="324" width="46" height="24" rx="5" fill={c.surface} fillOpacity="0.78" />
      {[220, 320, 470, 595].map((x) => <circle key={x} cx={x} cy="395" r="20" fill={c.text} fillOpacity="0.3" />)}
      <path d="M100 190H690L600 250H170Z" fill={c.motifMain} fillOpacity="0.16" />
    </g>
  );
}

function ControlTower({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <path d="M80 480 C210 130 390 540 650 110" stroke={c.motifMain} strokeWidth="5" strokeDasharray="18 16" strokeOpacity="0.42" />
      <path d="M80 210 C250 420 420 180 720 410" stroke={c.motifSoft} strokeWidth="4" strokeDasharray="10 16" strokeOpacity="0.42" />
      {[160, 380, 610].map((x, index) => (
        <circle key={x} cx={x} cy={index === 1 ? 280 : 190 + index * 70} r={index === 1 ? 58 : 40} fill={index === 1 ? c.motifMain : c.motifSoft} fillOpacity="0.22" />
      ))}
      <path d="M330 510H470M400 270V510M340 270H460L438 220H362Z" stroke={c.text} strokeWidth="10" strokeLinecap="round" strokeOpacity="0.34" />
    </g>
  );
}

function ExecutiveSlate({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <path d="M60 500H740" stroke={c.motifMain} strokeWidth="4" strokeOpacity="0.22" />
      {[90, 240, 390, 540].map((x, index) => (
        <rect key={x} x={x} y={260 - index * 36} width="92" height={220 + index * 36} rx="12" fill={c.motifMain} fillOpacity={0.16 + index * 0.025} />
      ))}
      <path d="M100 360Q230 260 380 310Q520 360 720 210" stroke={c.motifSoft} strokeWidth="6" strokeLinecap="round" strokeOpacity="0.28" />
    </g>
  );
}

function CoralService({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <path d="M80 470 C190 250 260 410 360 210 C460 40 600 170 720 80" stroke={c.motifSoft} strokeWidth="5" strokeDasharray="16 14" strokeOpacity="0.42" />
      {[80, 300, 520].map((x, index) => (
        <rect key={x} x={x} y={250 - index * 30} width={index === 1 ? 190 : 150} height="110" rx="24" fill={index === 1 ? c.motifMain : c.motifSoft} fillOpacity="0.22" />
      ))}
      <circle cx="160" cy="305" r="28" fill={c.accent} fillOpacity="0.22" />
      <path d="M145 305H175M160 290V320" stroke={c.text} strokeWidth="7" strokeLinecap="round" strokeOpacity="0.34" />
    </g>
  );
}

function MinimalGrid({ c, opacity, dotId }: { c: PatternColors; opacity: number; dotId: string }) {
  return (
    <g opacity={opacity}>
      <rect width="800" height="600" fill={`url(#${dotId})`} opacity="0.4" />
      <path d="M40 480 C190 270 330 520 500 260 C600 120 690 170 780 105" stroke={c.accent} strokeWidth="4" strokeDasharray="14 16" strokeOpacity="0.35" />
      <rect x="150" y="350" width="92" height="38" rx="6" fill={c.motifMain} fillOpacity="0.14" />
      <rect x="270" y="310" width="92" height="38" rx="6" fill={c.motifSoft} fillOpacity="0.16" />
      <rect x="390" y="380" width="92" height="38" rx="6" fill={c.motifMain} fillOpacity="0.12" />
      <path d="M90 180H260M90 220H190M520 170H710M520 210H640" stroke={c.motifMain} strokeWidth="6" strokeLinecap="round" strokeOpacity="0.16" />
    </g>
  );
}

function SmartInbox({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <rect x="80" y="150" width="230" height="150" rx="26" fill={c.motifMain} fillOpacity="0.18" />
      <path d="M108 188L195 245L282 188" stroke={c.text} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.34" />
      <rect x="450" y="110" width="200" height="300" rx="28" fill={c.motifSoft} fillOpacity="0.16" />
      <path d="M490 180H610M490 225H580M490 300H612M490 345H560" stroke={c.text} strokeWidth="8" strokeLinecap="round" strokeOpacity="0.28" />
      <path d="M310 230C380 205 380 335 450 310" stroke={c.accent} strokeWidth="5" strokeDasharray="14 12" strokeOpacity="0.42" />
    </g>
  );
}

function CustomsDocs({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <rect x="140" y="100" width="220" height="320" rx="22" fill={c.motifSoft} fillOpacity="0.16" />
      <path d="M190 170H310M190 220H275M190 270H315M190 320H260" stroke={c.text} strokeWidth="8" strokeLinecap="round" strokeOpacity="0.3" />
      <rect x="430" y="160" width="160" height="220" rx="12" fill={c.motifMain} fillOpacity="0.13" stroke={c.motifMain} strokeOpacity="0.25" strokeWidth="5" />
      <circle cx="510" cy="430" r="62" fill={c.accent} fillOpacity="0.18" />
      <path d="M480 430L503 453L546 398" stroke={c.text} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.36" />
    </g>
  );
}

function DepotGate({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <path d="M40 505H760" stroke={c.text} strokeWidth="14" strokeLinecap="round" strokeOpacity="0.22" />
      <path d="M190 110V500M610 110V500M170 110H630" stroke={c.motifMain} strokeWidth="12" strokeLinecap="round" strokeOpacity="0.32" />
      <rect x="285" y="300" width="210" height="72" rx="9" fill={c.motifSoft} fillOpacity="0.28" />
      <rect x="510" y="320" width="92" height="58" rx="14" fill={c.motifMain} fillOpacity="0.25" />
      <circle cx="330" cy="405" r="18" fill={c.text} fillOpacity="0.28" />
      <circle cx="565" cy="405" r="18" fill={c.text} fillOpacity="0.28" />
    </g>
  );
}

function RouteAtlas({ c, opacity, dotId }: { c: PatternColors; opacity: number; dotId: string }) {
  return (
    <g opacity={opacity}>
      <rect width="800" height="600" fill={`url(#${dotId})`} opacity="0.5" />
      <path d="M80 470 C190 90 350 410 470 180 C570 -10 690 120 750 60" stroke={c.motifMain} strokeWidth="6" strokeDasharray="18 18" strokeOpacity="0.42" />
      {[80, 300, 470, 750].map((x, index) => (
        <circle key={x} cx={x} cy={[470, 290, 180, 60][index]} r="24" fill={c.motifSoft} fillOpacity="0.28" />
      ))}
      <path d="M140 150H310M120 220H230M500 380H700" stroke={c.motifMain} strokeWidth="8" strokeLinecap="round" strokeOpacity="0.15" />
    </g>
  );
}

function ColdChain({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <rect x="150" y="320" width="350" height="90" rx="18" fill={c.motifSoft} fillOpacity="0.2" />
      <rect x="525" y="342" width="110" height="70" rx="16" fill={c.motifMain} fillOpacity="0.18" />
      {[240, 480, 590].map((x) => <circle key={x} cx={x} cy="445" r="22" fill={c.text} fillOpacity="0.25" />)}
      <g stroke={c.motifMain} strokeWidth="6" strokeLinecap="round" strokeOpacity="0.42">
        <path d="M330 120V290M250 168L410 242M410 168L250 242" />
        <path d="M540 130V260M490 165L590 225M590 165L490 225" />
      </g>
      <path d="M90 120C260 70 430 130 730 75" stroke={c.motifSoft} strokeWidth="5" strokeDasharray="14 16" strokeOpacity="0.34" />
    </g>
  );
}

function AirCargo({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <path d="M70 450 C240 160 470 220 730 80" stroke={c.motifMain} strokeWidth="5" strokeDasharray="18 18" strokeOpacity="0.36" />
      <path d="M210 340L570 190C602 176 630 205 604 232L520 314L660 390L615 425L450 385L340 500L285 485L345 360L190 365Z" fill={c.motifSoft} fillOpacity="0.2" stroke={c.text} strokeOpacity="0.28" strokeWidth="6" strokeLinejoin="round" />
      <rect x="90" y="475" width="100" height="48" rx="10" fill={c.motifMain} fillOpacity="0.16" />
      <rect x="220" y="455" width="100" height="48" rx="10" fill={c.motifSoft} fillOpacity="0.18" />
    </g>
  );
}

function LedgerFlow({ c, opacity }: { c: PatternColors; opacity: number }) {
  return (
    <g opacity={opacity}>
      <rect x="110" y="150" width="200" height="260" rx="28" fill={c.motifMain} fillOpacity="0.12" />
      <rect x="490" y="150" width="200" height="260" rx="28" fill={c.motifSoft} fillOpacity="0.14" />
      <path d="M160 220H260M160 270H240M160 320H270M540 220H640M540 270H620M540 320H650" stroke={c.text} strokeWidth="8" strokeLinecap="round" strokeOpacity="0.28" />
      <path d="M330 280H455" stroke={c.accent} strokeWidth="12" strokeLinecap="round" strokeOpacity="0.44" />
      <path d="M430 245L468 280L430 315" stroke={c.accent} strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.44" />
      <circle cx="400" cy="420" r="58" fill={c.motifSoft} fillOpacity="0.16" />
      <path d="M374 420H426M400 394V446" stroke={c.text} strokeWidth="9" strokeLinecap="round" strokeOpacity="0.34" />
    </g>
  );
}

function AiNetwork({ c, opacity }: { c: PatternColors; opacity: number }) {
  const nodes = [
    [170, 180, 52],
    [380, 280, 70],
    [600, 160, 46],
    [580, 435, 54],
    [210, 450, 42],
  ] as const;
  return (
    <g opacity={opacity}>
      <path d="M170 180L380 280L600 160M380 280L580 435M380 280L210 450" stroke={c.motifMain} strokeWidth="7" strokeLinecap="round" strokeOpacity="0.25" />
      {nodes.map(([x, y, r], index) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill={index === 1 ? c.motifMain : c.motifSoft} fillOpacity={index === 1 ? 0.2 : 0.16} />
      ))}
      <path d="M350 280H410M380 250V310" stroke={c.text} strokeWidth="9" strokeLinecap="round" strokeOpacity="0.36" />
      <path d="M145 180H195M575 160H625M555 435H605" stroke={c.text} strokeWidth="6" strokeLinecap="round" strokeOpacity="0.22" />
    </g>
  );
}
